'use client';

import React, { useEffect, useState, useLayoutEffect } from 'react';
import Link from 'next/link';
import { createPortal } from 'react-dom';
import { NavOption } from '../../types';

interface NavDropdownProps {
  containerRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  open: boolean;
  onClose: () => void;
  options: NavOption[];
  testId: string;
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  containerRef,
  dropdownRef,
  open,
  onClose,
  options,
  testId,
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [open, containerRef, dropdownRef, onClose]);

  useLayoutEffect(() => {
    if (open && containerRef.current && dropdownRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight || 0;
      setPosition({
        top: rect.top + 4 + rect.height / 2 - dropdownHeight / 2,
        left: rect.right + 5,
      });

      // Focus first link when dropdown opens
      const firstLink = dropdownRef.current.querySelector('a');
      if (firstLink) {
        firstLink.focus();
      }
    }
  }, [open, containerRef, dropdownRef]);

  const handleItemClick = () => {
    onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      style={{ top: position.top, left: position.left }}
      className="min-w-40 fixed bg-white border border-neutral-200 shadow-lg rounded-lg z-[9999] p-1.5"
      data-testid={testId}
    >
      <div className="flex flex-col justify-start items-start">
        {options.map((option) => (
          <Link
            key={option.path}
            href={option.path}
            className="self-stretch p-2 rounded-lg inline-flex justify-start items-start gap-2 hover:bg-neutral-100 cursor-pointer"
            onClick={handleItemClick}
            data-testid={`${testId}-item-${option.label}`}
          >
            <span className="text-text-primary text-sm font-medium font-['Inter'] leading-5 whitespace-nowrap">
              {option.label}
            </span>
          </Link>
        ))}
      </div>
    </div>,
    document.body
  );
};

export default NavDropdown;
